import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Download, AlertTriangle } from 'lucide-react';

interface TimeStretchEditorProps {
  audioUrl?: string;
  onApply?: (stretchedUrl: string, ratio: number) => void;
  onClose?: () => void;
}

export const TimeStretchEditor: React.FC<TimeStretchEditorProps> = ({
  audioUrl: initialAudioUrl,
  onApply,
  onClose,
}) => {
  // State
  const [audioUrl, setAudioUrl] = useState<string | null>(initialAudioUrl || null);
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null);
  const [waveform, setWaveform] = useState<number[]>([]);
  const [ratio, setRatio] = useState<number>(1.0);
  const [preservePitch, setPreservePitch] = useState<boolean>(true);
  const [quality, setQuality] = useState<'draft' | 'standard' | 'high'>('standard');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  // Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Subtask 5.1: Load and display audio
  useEffect(() => {
    if (audioUrl) {
      loadAudio(audioUrl);
    }
  }, [audioUrl]);

  useEffect(() => {
    if (audioBuffer) {
      generateWaveform(audioBuffer);
    }
  }, [audioBuffer]);

  useEffect(() => {
    if (waveform.length > 0) {
      drawWaveform();
    }
  }, [waveform]);

  // Check if ratio exceeds 200% (2.0x) for warning
  useEffect(() => {
    setShowWarning(ratio > 2.0);
  }, [ratio]);

  // Cleanup
  useEffect(() => {
    return () => {
      stopPlayback();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const loadAudio = async (url: string) => {
    try {
      setError(null);
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      
      const audioContext = new AudioContext();
      const buffer = await audioContext.decodeAudioData(arrayBuffer);
      
      audioContextRef.current = audioContext;
      setAudioBuffer(buffer);
    } catch (err) {
      console.error('Failed to load audio:', err);
      setError('Failed to load audio file. Please try again.');
    }
  };

  const generateWaveform = async (buffer: AudioBuffer) => {
    try {
      const channelData = buffer.getChannelData(0);
      const samples = 200; // Number of waveform bars
      const blockSize = Math.floor(channelData.length / samples);
      const waveformData: number[] = [];

      for (let i = 0; i < samples; i++) {
        const start = i * blockSize;
        let sum = 0;
        
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[start + j]);
        }
        
        waveformData.push(sum / blockSize);
      }

      // Normalize
      const max = Math.max(...waveformData);
      const normalized = waveformData.map((v) => v / max);
      
      setWaveform(normalized);
    } catch (err) {
      console.error('Failed to generate waveform:', err);
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas || waveform.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const barWidth = width / waveform.length;
    const centerY = height / 2;

    ctx.clearRect(0, 0, width, height);

    // Draw waveform
    ctx.fillStyle = '#3b82f6';
    waveform.forEach((value, index) => {
      const barHeight = value * centerY * 0.9;
      const x = index * barWidth;
      
      // Draw top half
      ctx.fillRect(x, centerY - barHeight, barWidth - 1, barHeight);
      // Draw bottom half (mirrored)
      ctx.fillRect(x, centerY, barWidth - 1, barHeight);
    });

    // Draw center line
    ctx.strokeStyle = '#52525b';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.stroke();
  };

  // Subtask 5.2: Implement time stretch controls
  const handleRatioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRatio = parseFloat(e.target.value);
    // Clamp ratio between 0.25 and 4.0
    const clampedRatio = Math.max(0.25, Math.min(4.0, newRatio));
    setRatio(clampedRatio);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    }
  };

  const playPreview = () => {
    if (!audioBuffer || !audioContextRef.current) return;

    stopPlayback();

    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    
    // Apply playback rate for time stretching
    // Note: This changes both tempo and pitch
    // For true pitch-preserving time stretch, we'd need a more sophisticated algorithm
    source.playbackRate.value = ratio;
    
    source.connect(audioContextRef.current.destination);
    source.start(0);
    
    source.onended = () => {
      setIsPlaying(false);
      sourceNodeRef.current = null;
    };

    sourceNodeRef.current = source;
    setIsPlaying(true);
  };

  const stopPlayback = () => {
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
      } catch (err) {
        // Already stopped
      }
      sourceNodeRef.current = null;
    }
    setIsPlaying(false);
  };

  // Subtask 5.3: Apply time stretching
  const applyTimeStretch = async () => {
    if (!audioBuffer || !audioContextRef.current) return;

    setIsProcessing(true);
    setProgress(0);
    setError(null);

    try {
      // Create offline audio context for rendering
      const offlineContext = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        Math.ceil(audioBuffer.length / ratio),
        audioBuffer.sampleRate
      );

      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;
      source.playbackRate.value = ratio;
      source.connect(offlineContext.destination);
      source.start(0);

      // Simulate progress (offline rendering doesn't provide real progress)
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      // Render the stretched audio
      const renderedBuffer = await offlineContext.startRendering();
      
      clearInterval(progressInterval);
      setProgress(100);

      // Convert to WAV blob
      const wavBlob = audioBufferToWav(renderedBuffer);
      const stretchedUrl = URL.createObjectURL(wavBlob);

      // Update the current audio
      setAudioUrl(stretchedUrl);
      setAudioBuffer(renderedBuffer);

      // Callback with result
      if (onApply) {
        onApply(stretchedUrl, ratio);
      }

      setIsProcessing(false);
      setProgress(0);
    } catch (err) {
      console.error('Time stretch failed:', err);
      setError('Failed to process audio. Please try again.');
      setIsProcessing(false);
      setProgress(0);
    }
  };

  // Convert AudioBuffer to WAV blob
  const audioBufferToWav = (buffer: AudioBuffer): Blob => {
    const numberOfChannels = buffer.numberOfChannels;
    const length = buffer.length * numberOfChannels * 2;
    const arrayBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(arrayBuffer);

    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length, true);

    // Write audio data
    const channels: Float32Array[] = [];
    for (let i = 0; i < numberOfChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  const downloadStretched = () => {
    if (!audioUrl) return;

    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = `stretched_${ratio.toFixed(2)}x.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="p-6 bg-zinc-900 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white">Time Stretch Editor</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* File Input */}
      {!audioUrl && (
        <div className="mb-6">
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Load Audio File
          </button>
        </div>
      )}

      {/* Waveform Display */}
      {audioBuffer && (
        <div className="mb-6">
          <div className="bg-zinc-800 rounded-lg p-4">
            <canvas
              ref={canvasRef}
              width={600}
              height={120}
              className="w-full h-[120px]"
            />
            <div className="mt-2 text-sm text-zinc-400 text-center">
              Duration: {audioBuffer.duration.toFixed(2)}s → {(audioBuffer.duration / ratio).toFixed(2)}s
            </div>
          </div>
        </div>
      )}

      {/* Time Stretch Controls */}
      {audioBuffer && (
        <div className="space-y-6">
          {/* Ratio Slider */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Time Stretch Ratio: {ratio.toFixed(2)}x
            </label>
            <input
              type="range"
              min="0.25"
              max="4.0"
              step="0.01"
              value={ratio}
              onChange={handleRatioChange}
              className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-zinc-500 mt-1">
              <span>0.25x (Slower)</span>
              <span>1.0x (Original)</span>
              <span>4.0x (Faster)</span>
            </div>
          </div>

          {/* Quality Warning */}
          {showWarning && (
            <div className="flex items-start gap-2 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-200">
                <strong>Quality Warning:</strong> Stretch ratios above 2.0x may result in audio quality degradation.
              </div>
            </div>
          )}

          {/* Preserve Pitch Toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
            <div>
              <div className="text-sm font-medium text-zinc-300">Preserve Pitch</div>
              <div className="text-xs text-zinc-500">
                {preservePitch ? 'Enabled (recommended)' : 'Disabled'}
              </div>
            </div>
            <button
              onClick={() => setPreservePitch(!preservePitch)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preservePitch ? 'bg-blue-600' : 'bg-zinc-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preservePitch ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Quality Selector */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Processing Quality
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['draft', 'standard', 'high'] as const).map((q) => (
                <button
                  key={q}
                  onClick={() => setQuality(q)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    quality === q
                      ? 'bg-blue-600 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {q.charAt(0).toUpperCase() + q.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600/30 rounded-lg text-sm text-red-200">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={isPlaying ? stopPlayback : playPreview}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {isPlaying ? (
                <>
                  <Pause className="w-5 h-5" />
                  Stop Preview
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Preview
                </>
              )}
            </button>

            <button
              onClick={applyTimeStretch}
              disabled={isProcessing || ratio === 1.0}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
            >
              {isProcessing ? `Processing... ${progress}%` : 'Apply Time Stretch'}
            </button>

            {audioUrl && ratio !== 1.0 && !isProcessing && (
              <button
                onClick={downloadStretched}
                className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                title="Download stretched audio"
              >
                <Download className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Info Text */}
      {!audioBuffer && audioUrl && (
        <div className="text-center text-zinc-400 py-8">
          Loading audio...
        </div>
      )}

      {!audioUrl && (
        <div className="text-center text-zinc-400 py-8">
          <p className="mb-2">Load an audio file to begin time stretching.</p>
          <p className="text-sm text-zinc-500">
            Supported formats: MP3, WAV, OGG, M4A
          </p>
        </div>
      )}
    </div>
  );
};
